/**
 * Riester Fonts API Client
 * Centralized HTTP client with JWT auth for all backend calls.
 */

// In dev, Vite proxies /api → localhost:3001 (no CORS needed)
// In production, set VITE_API_URL to your API server
const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// ─── Token Management ──────────────────────────────────

let token = localStorage.getItem('fontvault_token');
let refreshToken = localStorage.getItem('fontvault_refresh_token');
let onAuthError = null; // callback for 401s (logout)
let refreshPromise = null; // dedup concurrent refresh attempts

export function setToken(t) {
  token = t;
  if (t) {
    localStorage.setItem('fontvault_token', t);
  } else {
    localStorage.removeItem('fontvault_token');
  }
}

export function setRefreshToken(t) {
  refreshToken = t;
  if (t) {
    localStorage.setItem('fontvault_refresh_token', t);
  } else {
    localStorage.removeItem('fontvault_refresh_token');
  }
}

export function getToken() {
  return token;
}

export function setOnAuthError(callback) {
  onAuthError = callback;
}

// ─── Token Refresh ────────────────────────────────────

async function attemptRefresh() {
  if (!refreshToken) return false;

  // Dedup: if a refresh is already in flight, wait for it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ─── Base Fetch ────────────────────────────────────────

async function request(path, options = {}, _isRetry = false) {
  const url = `${API_BASE}${path}`;
  const headers = { ...options.headers };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 — try refresh once before giving up
  if (res.status === 401 && !_isRetry) {
    const data = await res.json().catch(() => ({}));
    if (data.code === 'TOKEN_EXPIRED' || token) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return request(path, options, true);
      }
    }
    setToken(null);
    setRefreshToken(null);
    onAuthError?.();
    throw new ApiError('Session expired', 401);
  }

  if (res.status === 401) {
    setToken(null);
    setRefreshToken(null);
    onAuthError?.();
    throw new ApiError('Session expired', 401);
  }

  // Parse response
  let data;
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.blob();
  }

  if (!res.ok) {
    throw new ApiError(data?.error || `Request failed (${res.status})`, res.status, data);
  }

  return data;
}

function get(path, params) {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return request(`${path}${query}`);
}

function post(path, body) {
  if (body instanceof FormData) {
    return request(path, { method: 'POST', body });
  }
  return request(path, { method: 'POST', body: JSON.stringify(body) });
}

function put(path, body) {
  return request(path, { method: 'PUT', body: JSON.stringify(body) });
}

function del(path) {
  return request(path, { method: 'DELETE' });
}

// ─── Auth ──────────────────────────────────────────────

export const auth = {
  login: (email, password) => post('/auth/login', { email, password }),
  me: () => get('/auth/me'),
};

// ─── Fonts ─────────────────────────────────────────────

export const fonts = {
  list: (params = {}) => get('/fonts', params),

  get: (id) => get(`/fonts/${id}`),

  upload: (files, collectionId, licenseFiles, options = {}) => {
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    for (const file of (licenseFiles || [])) formData.append('license_files', file);
    if (collectionId) formData.append('collectionId', collectionId);
    if (options.replaceIds?.length) formData.append('replaceIds', JSON.stringify(options.replaceIds));
    return post('/fonts/upload', formData);
  },

  checkDuplicates: (fileList) => post('/fonts/check-duplicates', { files: fileList }),

  delete: (id) => del(`/fonts/${id}`),

  toggleFavorite: (id) => put(`/fonts/${id}/favorite`),

  getDownloadUrl: (fontId, fileId) =>
    `${API_BASE}/fonts/${fontId}/download/${fileId}`,

  serveUrl: (fontId, fileId) =>
    `${API_BASE}/fonts/${fontId}/serve/${fileId}`,

  downloadLicensePdf: async (fontId) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/fonts/${fontId}/license-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('License PDF not available');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'license.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },

  alphabet: () => get('/fonts/alphabet'),

  bulkDelete: (fontIds) => post('/fonts/bulk-delete', { fontIds }),

  bulkTag: (fontIds, tagIds) => post('/fonts/bulk-tag', { fontIds, tagIds }),

  addTags: (fontId, tagIds) => post(`/fonts/${fontId}/tags`, { tagIds }),

  removeTag: (fontId, tagId) => del(`/fonts/${fontId}/tags/${tagId}`),

  similar: (fontId) => get(`/fonts/${fontId}/similar`),

  rate: (fontId, rating) => put(`/fonts/${fontId}/rate`, { rating }),

  removeRating: (fontId) => del(`/fonts/${fontId}/rate`),

  duplicates: () => get('/fonts/duplicates'),

  health: () => get('/fonts/health'),

  healthScan: (fontIds) => post('/fonts/health/scan', { fontIds }),

  collect: async (fontIds) => {
    const url = `${API_BASE}/fonts/collect`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fontIds }),
    });
    if (!res.ok) throw new ApiError('ZIP export failed', res.status);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'fonts-collection.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },
};

// ─── Tags ─────────────────────────────────────────────

export const tags = {
  list: () => get('/tags'),
  create: (data) => post('/tags', data),
  delete: (id) => del(`/tags/${id}`),
};

// ─── Collections ───────────────────────────────────────

export const collections = {
  list: () => get('/collections'),
  create: (data) => post('/collections', data),
  update: (id, data) => put(`/collections/${id}`, data),
  delete: (id) => del(`/collections/${id}`),
  addFonts: (id, fontIds) => post(`/collections/${id}/fonts`, { fontIds }),
  removeFont: (collectionId, fontId) => del(`/collections/${collectionId}/fonts/${fontId}`),
  fontIds: (id) => get(`/collections/${id}/font-ids`),
};

// ─── Google Fonts ─────────────────────────────────────

export const googlefonts = {
  list: (params = {}) => get('/googlefonts', params),
  import: (data) => post('/googlefonts/import', data),
};

// ─── Adobe Fonts ──────────────────────────────────────

export const adobefonts = {
  list: (params = {}) => get('/adobefonts', params),
  import: (data) => post('/adobefonts/import', data),
};

// ─── AI ──────────────────────────────────────────────

export const ai = {
  smartSearch: (query) => post('/ai/smart-search', { query }),
  pairings: (fontId) => get(`/ai/pairings/${fontId}`),
  licenseSummary: (fontId) => get(`/ai/license-summary/${fontId}`),
};

// ─── Activations ───────────────────────────────────────

export const activations = {
  list: () => get('/activations'),
  toggle: (fontId, active, deviceId) =>
    put(`/activations/${fontId}`, { active, deviceId }),
  bulk: (fontIds, active, source, sourceId) =>
    post('/activations/bulk', { fontIds, active, source, sourceId }),
  sync: (deviceId, currentFontIds, lastSyncAt) =>
    post('/activations/sync', { deviceId, currentFontIds, lastSyncAt }),
};

// ─── Users ─────────────────────────────────────────────

export const users = {
  list: (params = {}) => get('/users', params),
  get: (id) => get(`/users/${id}`),
  update: (id, data) => put(`/users/${id}`, data),
  updateRole: (id, role) => put(`/users/${id}/role`, { role }),
  deactivate: (id) => del(`/users/${id}`),
  reactivate: (id) => put(`/users/${id}/reactivate`),
  invite: (data) => post('/users/invite', data),
  resetPassword: (id, newPassword) => put(`/users/${id}/reset-password`, { newPassword }),
  activity: (id, params = {}) => get(`/users/${id}/activity`, params),
};

// ─── Stats ─────────────────────────────────────────────

export const stats = {
  dashboard: () => get('/stats'),
  activity: (params = {}) => get('/stats/activity', params),
};

// ─── Health ────────────────────────────────────────────

export const health = () => get('/health');

// ─── Brand Kits ───────────────────────────────────────

export const brandkits = {
  list: () => get('/brand-kits'),
  get: (id) => get(`/brand-kits/${id}`),
  create: (data) => post('/brand-kits', data),
  update: (id, data) => put(`/brand-kits/${id}`, data),
  remove: (id) => del(`/brand-kits/${id}`),
  addFonts: (id, fontIds, usageNote) => post(`/brand-kits/${id}/fonts`, { fontIds, usageNote }),
  removeFont: (id, fontId) => del(`/brand-kits/${id}/fonts/${fontId}`),
  updateFontNote: (id, fontId, usageNote) => put(`/brand-kits/${id}/fonts/${fontId}`, { usageNote }),
};

export default {
  auth,
  fonts,
  tags,
  collections,
  ai,
  activations,
  users,
  stats,
  health,
  googlefonts,
  adobefonts,
  brandkits,
  setToken,
  setRefreshToken,
  getToken,
  setOnAuthError,
};
